import { useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// FocalPointPicker — drag-to-reposition focal-point control for cropped
// images. Non-destructive: we never re-crop the file, we just store a CSS
// object-position value ("x% y%") and apply it on display.
//
// The framed preview below IS the real display (WYSIWYG) — same
// object-cover + object-position combo the live site uses. Fully controlled:
// the caller owns `value` and re-renders with the new position on every
// onChange call, so the frame always shows exactly what's about to be saved.
//
// Drag model: dragging the image DOWN reveals the TOP (y% -> 0); dragging UP
// reveals the bottom (y% -> 100). Same logic on the x axis (drag right ->
// reveals the left edge, x% -> 0).
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_POSITION = '50% 50%';

// Cap the preview frame's HEIGHT so a wide banner or (worse) a square logo can't
// swallow the whole editor. Width follows the aspect off this height but never
// exceeds the container. Same crop ratio as the live display — just compact.
const FRAME_MAX_H = '8rem';

function clampPct(n: number): number {
  return Math.min(100, Math.max(0, n));
}

function parsePosition(pos: string | undefined): { x: number; y: number } {
  const parts = (pos ?? DEFAULT_POSITION).trim().split(/\s+/);
  const x = clampPct(parseFloat(parts[0]) || 0);
  const y = clampPct(parseFloat(parts[1] ?? '') || 0);
  return { x, y };
}

export function FocalPointPicker({
  src,
  aspect,
  value,
  onChange,
  label,
}: {
  src: string;
  aspect: number;
  value?: string;
  onChange: (pos: string) => void;
  label?: string;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  if (!src) return null;

  const position = value ?? DEFAULT_POSITION;
  const { x: markerX, y: markerY } = parsePosition(position);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    const { x, y } = parsePosition(value);
    drag.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, startPosX: x, startPosY: y };
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const d = drag.current;
    const frame = frameRef.current;
    if (!d || !frame || d.pointerId !== e.pointerId) return;
    const rect = frame.getBoundingClientRect();
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const newX = clampPct(Math.round(d.startPosX - (dx / rect.width) * 100));
    const newY = clampPct(Math.round(d.startPosY - (dy / rect.height) * 100));
    onChange(`${newX}% ${newY}%`);
  }

  function endDrag(e: React.PointerEvent<HTMLDivElement>) {
    if (drag.current?.pointerId !== e.pointerId) return;
    drag.current = null;
    setIsDragging(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
  }

  return (
    <div className="space-y-1.5">
      {label && (
        <p className="mb-1 font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-500">
          {label}
        </p>
      )}

      <div
        ref={frameRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="relative select-none overflow-hidden border border-iron-800 bg-iron-950"
        style={{ width: `min(100%, calc(${FRAME_MAX_H} * ${aspect}))`, aspectRatio: aspect, touchAction: 'none', cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          className="pointer-events-none h-full w-full object-cover"
          style={{ objectPosition: position }}
        />
        {/* Focal-point marker */}
        <div
          className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-forge-400 bg-forge-500/50 shadow-[0_0_0_1px_rgba(0,0,0,0.6)]"
          style={{ left: `${markerX}%`, top: `${markerY}%` }}
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] text-iron-500">
          Drag the image to reposition — this frame is exactly how it'll display.
        </p>
        <button
          type="button"
          onClick={() => onChange(DEFAULT_POSITION)}
          className="flex shrink-0 items-center gap-1 font-display text-[9px] font-black uppercase tracking-wider text-iron-500 transition-colors hover:text-forge-300"
        >
          <RotateCcw className="h-3 w-3" strokeWidth={2.5} />
          Reset to centre
        </button>
      </div>
    </div>
  );
}
